export const DEFAULT_TEAM_SCHEDULE_FEEDS = [
  'https://ical-cdn.teamsnap.com/team_schedule/0f73a0ef-6ac8-4e8d-a17b-38884fadbc2f.ics',
  'https://ical-cdn.teamsnap.com/team_schedule/f932eca9-a630-4059-8ad7-7c611529a6f0.ics',
];

export function cleanCalendarDescription(raw = '') {
  return String(raw)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function inferEventType(title = '') {
  const t = String(title).toLowerCase();
  if (t.includes('tournament')) return 'Tournament';
  if (t.includes('dryland')) return 'Dryland';
  if (t.includes('game')) return 'Game';
  if (t.includes('practice')) return 'Practice';
  if (t.includes('team') || t.includes('meeting')) return 'Team Event';
  return 'Event';
}

/** Matches Cloud Function mapping from TeamSnap feed URL */
export function inferTeamLabelFromFeed(sourceFeed = '') {
  const u = String(sourceFeed);
  if (u.includes('0f73a0ef-6ac8-4e8d-a17b-38884fadbc2f')) return '14U';
  if (u.includes('f932eca9-a630-4059-8ad7-7c611529a6f0')) return '12U';
  return '';
}

export function teamLabelForEvent(event) {
  if (event?.teamLabel) return event.teamLabel;
  return inferTeamLabelFromFeed(event?.sourceFeed || '');
}

/** Parent filter buckets: practice (incl. dryland), games (incl. tournament), other team events */
export function getScheduleFilterBucket(event) {
  const type = String(event?.type || '');
  const title = String(event?.title || '').toLowerCase();
  if (type === 'Practice' || type === 'Dryland' || title.includes('dryland')) return 'practice';
  if (type === 'Game' || type === 'Tournament') return 'games';
  return 'events';
}

export function filterScheduleEvents(events, { teamFilter, typePractice, typeGames, typeEvents }) {
  const list = Array.isArray(events) ? events : [];
  const noTypeSelected = !typePractice && !typeGames && !typeEvents;
  return list.filter((ev) => {
    const label = teamLabelForEvent(ev);
    if (teamFilter === '12U' && label !== '12U') return false;
    if (teamFilter === '14U' && label !== '14U') return false;
    if (noTypeSelected) return true;
    const bucket = getScheduleFilterBucket(ev);
    if (bucket === 'practice' && typePractice) return true;
    if (bucket === 'games' && typeGames) return true;
    if (bucket === 'events' && typeEvents) return true;
    return false;
  });
}

/** Preset keys for schedule "Show" dropdown (maps to practice/games/events buckets). */
export function typeBoolsFromShowFilter(showFilter) {
  switch (String(showFilter || 'all')) {
    case 'practice':
      return { typePractice: true, typeGames: false, typeEvents: false };
    case 'games':
      return { typePractice: false, typeGames: true, typeEvents: false };
    case 'events':
      return { typePractice: false, typeGames: false, typeEvents: true };
    case 'practice_games':
      return { typePractice: true, typeGames: true, typeEvents: false };
    case 'practice_events':
      return { typePractice: true, typeGames: false, typeEvents: true };
    case 'games_events':
      return { typePractice: false, typeGames: true, typeEvents: true };
    case 'all':
    default:
      return { typePractice: true, typeGames: true, typeEvents: true };
  }
}

export function startOfWeekSunday(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  x.setDate(x.getDate() - day);
  return x;
}

export function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function localDayKey(iso) {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function mapsUrlForLocation(location = '') {
  const trimmed = String(location).trim();
  if (!trimmed) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmed)}`;
}

export function addToMyCalendarHref(event) {
  if (!event) return '';
  const details = new URLSearchParams();
  details.set('text', event.title || 'Team event');
  details.set('details', event.description || '');
  if (event.location) details.set('location', event.location);
  if (event.startIso && event.endIso) {
    const start = event.startIso.replace(/[-:]/g, '').replace('.000', '');
    const end = event.endIso.replace(/[-:]/g, '').replace('.000', '');
    details.set('dates', `${start}/${end}`);
  }
  return `https://calendar.google.com/calendar/u/0/r/eventedit?${details.toString()}`;
}

export function formatEventDate(iso) {
  if (!iso) return 'TBD';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return 'TBD';
  return dt.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export function formatEventTimeRange(event) {
  if (!event?.startIso) return 'Time TBD';
  if (event.allDay) return 'All day';
  const start = new Date(event.startIso);
  if (Number.isNaN(start.getTime())) return 'Time TBD';
  const startLabel = start.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (!event.endIso) return startLabel;
  const end = new Date(event.endIso);
  if (Number.isNaN(end.getTime())) return startLabel;
  const endLabel = end.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${startLabel} - ${endLabel}`;
}

function weekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - day);
  return d;
}

function parseEventDate(event) {
  return new Date(event.startIso || event.startDate || Date.now());
}

export function groupEventsByProximity(events, now = new Date()) {
  const sorted = [...events].sort((a, b) => parseEventDate(a) - parseEventDate(b));
  const startToday = new Date(now);
  startToday.setHours(0, 0, 0, 0);
  const startTomorrow = new Date(startToday);
  startTomorrow.setDate(startTomorrow.getDate() + 1);
  const startDayAfterTomorrow = new Date(startTomorrow);
  startDayAfterTomorrow.setDate(startDayAfterTomorrow.getDate() + 1);
  const thisWeekStart = weekStart(now);
  const nextWeekStart = new Date(thisWeekStart);
  nextWeekStart.setDate(nextWeekStart.getDate() + 7);
  const weekAfterNextStart = new Date(nextWeekStart);
  weekAfterNextStart.setDate(weekAfterNextStart.getDate() + 7);

  const groups = {
    today: [],
    tomorrow: [],
    thisWeek: [],
    nextWeek: [],
    later: [],
  };

  sorted.forEach((event) => {
    const dt = parseEventDate(event);
    if (Number.isNaN(dt.getTime())) return;
    if (dt >= startToday && dt < startTomorrow) {
      groups.today.push(event);
      return;
    }
    if (dt >= startTomorrow && dt < startDayAfterTomorrow) {
      groups.tomorrow.push(event);
      return;
    }
    if (dt >= startDayAfterTomorrow && dt < nextWeekStart) {
      groups.thisWeek.push(event);
      return;
    }
    if (dt >= nextWeekStart && dt < weekAfterNextStart) {
      groups.nextWeek.push(event);
      return;
    }
    groups.later.push(event);
  });

  return groups;
}

function unfoldIcsLines(raw) {
  return String(raw || '').replace(/\r\n[ \t]/g, '').replace(/\r/g, '\n');
}

function parseIcsDate(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';
  if (/^\d{8}$/.test(value)) {
    const y = Number(value.slice(0, 4));
    const m = Number(value.slice(4, 6)) - 1;
    const d = Number(value.slice(6, 8));
    return new Date(y, m, d).toISOString();
  }
  if (/^\d{8}T\d{6}Z$/.test(value)) {
    const normalized = value.replace(
      /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
      '$1-$2-$3T$4:$5:$6Z'
    );
    return new Date(normalized).toISOString();
  }
  if (/^\d{8}T\d{6}$/.test(value)) {
    const y = Number(value.slice(0, 4));
    const m = Number(value.slice(4, 6)) - 1;
    const d = Number(value.slice(6, 8));
    const hh = Number(value.slice(9, 11));
    const mm = Number(value.slice(11, 13));
    const ss = Number(value.slice(13, 15));
    return new Date(y, m, d, hh, mm, ss).toISOString();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
}

function parseIcsEvent(block, feedUrl) {
  const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
  const row = {};
  lines.forEach((line) => {
    const sep = line.indexOf(':');
    if (sep <= 0) return;
    const key = line.slice(0, sep);
    const value = line.slice(sep + 1).trim();
    if (key.startsWith('SUMMARY')) row.summary = value;
    else if (key.startsWith('DESCRIPTION')) row.description = value;
    else if (key.startsWith('LOCATION')) row.location = value;
    else if (key.startsWith('UID')) row.uid = value;
    else if (key.startsWith('URL')) row.url = value;
    else if (key.startsWith('DTSTART')) row.startRaw = value;
    else if (key.startsWith('DTEND')) row.endRaw = value;
  });
  const startIso = parseIcsDate(row.startRaw);
  const endIso = parseIcsDate(row.endRaw);
  const allDay = row.startRaw && /^\d{8}$/.test(row.startRaw);
  return {
    id: row.uid || `${feedUrl}-${startIso}-${row.summary || 'event'}`,
    title: row.summary || 'Team event',
    startIso,
    endIso,
    allDay,
    location: row.location || '',
    description: cleanCalendarDescription(row.description || ''),
    htmlLink: row.url || '',
    sourceFeed: feedUrl,
    teamLabel: inferTeamLabelFromFeed(feedUrl),
    type: inferEventType(row.summary || ''),
  };
}

function parseIcsEvents(raw, feedUrl) {
  const text = unfoldIcsLines(raw);
  const matches = text.match(/BEGIN:VEVENT[\s\S]*?END:VEVENT/g) || [];
  return matches.map((block) => parseIcsEvent(block, feedUrl)).filter((e) => e.startIso);
}

function dedupeEvents(events) {
  const out = new Map();
  events.forEach((event) => {
    const key = `${event.title}|${event.startIso}|${event.location}`;
    if (!out.has(key)) out.set(key, event);
  });
  return [...out.values()];
}

function normalizeFeedUrl(url) {
  const trimmed = String(url || '').trim();
  if (!trimmed) return '';
  // TeamSnap links often get shared as http; prefer https to avoid mixed-content/CORS issues.
  return trimmed.replace(/^http:\/\//i, 'https://');
}

async function fetchFeedText(url, signal) {
  const directRes = await fetch(url, { signal });
  if (directRes.ok) return directRes.text();
  throw new Error(`Calendar feed request failed (${directRes.status})`);
}

async function fetchFeedTextWithFallback(url, signal) {
  try {
    return await fetchFeedText(url, signal);
  } catch (err) {
    // TeamSnap ICS feeds can block browser CORS. Try a read-only passthrough as a fallback.
    const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
    const proxiedRes = await fetch(proxyUrl, { signal });
    if (!proxiedRes.ok) {
      throw err;
    }
    return proxiedRes.text();
  }
}

export async function fetchUpcomingCalendarEvents({ feedUrls, weeksAhead = 8, max = 120, signal }) {
  const urls = (feedUrls || []).map((x) => normalizeFeedUrl(x)).filter(Boolean);
  if (!urls.length) return [];
  const now = new Date();
  const end = new Date(now);
  end.setDate(end.getDate() + weeksAhead * 7);
  const responses = await Promise.all(
    urls.map(async (url) => {
      const text = await fetchFeedTextWithFallback(url, signal);
      return parseIcsEvents(text, url);
    })
  );
  return dedupeEvents(responses.flat())
    .filter((event) => {
      const dt = new Date(event.startIso);
      return !Number.isNaN(dt.getTime()) && dt >= now && dt <= end;
    })
    .sort((a, b) => new Date(a.startIso) - new Date(b.startIso))
    .slice(0, max);
}
